/**
 * Sport clubs management page.
 * List with {@link SlidePanel} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { ManagementPage } from '@/components/common/ManagementPage';
import {
  useSportClubPageActions,
  sportClubsKeys,
} from '@/components/sport-clubs/sport-club-table-utils';
import { useDetailsView } from '@/hooks/useDetailsView';
import { CreateSportClubView } from '@/components/sport-clubs/CreateSportClubView';
import { EditSportClubView } from '@/components/sport-clubs/EditSportClubView';
import { SportClubsDataTable } from '@/components/sport-clubs/SportClubsDataTable';
import type { SportClub } from '@/types/sport-club.types';

/** Sport clubs list with {@link SlidePanel} create/edit. */
export const SportClubsManagementPage: React.FC = () => {
  const clubActions = useSportClubPageActions();
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editingClubId, setEditingClubId] = React.useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  /** Deep-link: open create/edit/view via URL params, then strip them. */
  React.useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('view');

    if (editId) {
      setEditingClubId(editId);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    } else if (viewId) {
      setOpenRow(viewId);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    } else if (shouldCreate) {
      setCreateOpen(true);
      closeRow();
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, closeRow, openRow]);

  const anyFormOpen = createOpen || editingClubId !== null;
  const prevAnyFormOpenRef = React.useRef(anyFormOpen);
  React.useEffect(() => {
    if (!prevAnyFormOpenRef.current && anyFormOpen && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevAnyFormOpenRef.current = anyFormOpen;
  }, [anyFormOpen]);

  const handleView = (club: SportClub) => {
    openRow(club.id);
  };

  const handleEdit = (club: SportClub) => {
    setEditingClubId(club.id);
    closeRow();
  };

  const handleOpenCreate = () => {
    setCreateOpen(true);
    closeRow();
  };

  const handleCloseCreate = () => {
    setCreateOpen(false);
  };

  const handleCloseEdit = () => {
    setEditingClubId(null);
  };

  const handleSuccess = () => {
    setCreateOpen(false);
    setEditingClubId(null);
    closeRow();
    void queryClient.invalidateQueries({ queryKey: sportClubsKeys.all });
  };

  return (
    <ManagementPage
      title="Gestion des Clubs de sport"
      subtitle="Créez, modifiez et gérez tous vos clubs de sport"
      contentRef={contentRef}
      actions={
        clubActions.canCreateClub ? (
          <Button preset="create-entity" onClick={handleOpenCreate}>
            Créer un club
          </Button>
        ) : undefined
      }
    >
      <SportClubsDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        pageActions={{
          canView: clubActions.canViewClub,
          canEdit: clubActions.canEditClub,
          canDelete: clubActions.canDeleteClub,
          canExportExcel: clubActions.canExportClubs,
        }}
        onView={handleView}
        onEdit={handleEdit}
      />

      <SlidePanel isOpen={createOpen} onClose={handleCloseCreate}>
        <CreateSportClubView onClose={handleCloseCreate} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editingClubId !== null} onClose={handleCloseEdit}>
        {editingClubId ? (
          <EditSportClubView
            key={editingClubId}
            clubId={editingClubId}
            onClose={handleCloseEdit}
            onSuccess={handleSuccess}
          />
        ) : null}
      </SlidePanel>
    </ManagementPage>
  );
};
